import { useTocContext } from "contexts/AppSettingsContext";
import { ReactNode, useEffect, useEffectEvent, useRef, useState } from "react";
import { FloppyFill } from "react-bootstrap-icons";
import "./TocSaveIndicator.css";

/**
 * Save indicator component that displays a flashing diskette icon when TOC changes
 * result in a minor version update. The indicator flashes for 2 seconds.
 */
export const TocSaveIndicator = (): ReactNode => {
  const { state } = useTocContext();
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const lastTriggeredAtRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const triggerTimerRef = useRef<number | null>(null);

  /**
   * Trigger the indicator in response to an external event.
   * Scheduled from an effect via a timer to avoid synchronous setState.
   */
  const triggerIndicator = useEffectEvent(() => {
    setShowIndicator(true);

    // Clear any previous hide timers and schedule dismissal in 2 seconds
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      setShowIndicator(false);
      hideTimerRef.current = null;
    }, 2000);
  });

  useEffect(() => {
    // React to a successful save completion timestamp rather than version maths
    if (
      state.sendUpdate &&
      !state.saveError &&
      state.lastMinorVersionSavedAt !== null &&
      state.lastMinorVersionSavedAt !== undefined
    ) {
      const prev = lastTriggeredAtRef.current;
      const hasChanged = prev !== state.lastMinorVersionSavedAt;

      // Trigger on every new save timestamp to restart the 2-second flash
      if (hasChanged) {
        lastTriggeredAtRef.current = state.lastMinorVersionSavedAt;
        // Clear any previous trigger timer
        if (triggerTimerRef.current !== null) {
          clearTimeout(triggerTimerRef.current);
        }
        // Schedule indicator update asynchronously to avoid React warning about synchronous setState in effect
        triggerTimerRef.current = window.setTimeout(() => {
          triggerIndicator();
          triggerTimerRef.current = null;
        }, 0);
      }
    }
  }, [state.lastMinorVersionSavedAt, state.sendUpdate, state.saveError]);

  // Cleanup timers on unmount
  useEffect(
    () => () => {
      if (triggerTimerRef.current !== null) {
        clearTimeout(triggerTimerRef.current);
      }
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  return (
    showIndicator && (
      <div
        className="toc-save-indicator"
        title="TOC saved"
      >
        <FloppyFill
          className="save-icon-flash toc-save-icon"
          size={16}
        />
      </div>
    )
  );
};

TocSaveIndicator.displayName = "TocSaveIndicator";
